import { Input } from "@/components/ui/input";
import { roundToTwoDecimals } from "@/lib/utils";
import * as React from "react";

export type FeeAmountInputProps = Omit<
  React.ComponentProps<typeof Input>,
  "value" | "onChange" | "onInput" | "onBlur" | "type"
> & {
  value: string;
  onValueChange: (value: string) => void;
  onBlurComplete?: () => void;
};

/** Currency-style fee field: raw value while typing, two decimals on blur. */
export function FeeAmountInput({
  value,
  onValueChange,
  onBlurComplete,
  disabled,
  className,
  ...props
}: FeeAmountInputProps) {
  const [isFocused, setIsFocused] = React.useState(false);
  const [draftValue, setDraftValue] = React.useState(value);

  React.useEffect(() => {
    if (!isFocused) {
      setDraftValue(value);
    }
  }, [value, isFocused]);

  const displayValue = isFocused
    ? draftValue
    : roundToTwoDecimals(value || "0");

  return (
    <Input
      type="number"
      min={0}
      step="0.01"
      value={displayValue}
      disabled={disabled}
      className={className}
      onFocus={() => {
        setDraftValue(value);
        setIsFocused(true);
      }}
      onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
        const next = e.target.value;
        setDraftValue(next);
        onValueChange(next);
      }}
      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);
        const formatted = roundToTwoDecimals(e.target.value || "0");
        setDraftValue(formatted);
        onValueChange(formatted);
        onBlurComplete?.();
      }}
      {...props}
    />
  );
}
